import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const EditableField = ({
  icon: Icon,
  label,
  value,
  isEditing,
  tempValue,
  onEdit,
  onSave,
  onCancel,
  onChange,
  updating,
  isAuditorView,
  type = 'text',
  options,
  renderDisplay,
  disabled = false,
}) => {
  const theme = useTheme();

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Icon sx={{ fontSize: 18, color: theme.customColors?.text?.secondary }} />
        <Typography variant="subtitle2">
          {label}
        </Typography>
        {!isEditing && !isAuditorView && !disabled && (
          <Tooltip title={`Edit ${label.toLowerCase()}`}>
            <span>
              <IconButton
                size="small"
                onClick={onEdit}
                disabled={updating}
                sx={{
                  p: 0.5,
                  color: theme.customColors?.text?.secondary,
                  "&:hover": { bgcolor: theme.customColors?.background?.light },
                  "&.Mui-disabled": {
                    color: theme.customColors?.text?.disabled || "#D1D5DB",
                  },
                }}
              >
                <EditIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
      {isEditing ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
          {type === 'select' ? (
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <Select
                value={tempValue || ""}
                onChange={(e) => onChange(e.target.value)}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem' }}>Select...</Typography>;
                  }
                  const option = options?.find(o => o.value === parseInt(selected));
                  return (
                    <Typography sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {option?.label || "Not Assigned"}
                    </Typography>
                  );
                }}
                sx={{
                  fontSize: '0.75rem',
                  height: '32px',
                  "& .MuiSelect-select": {
                    py: 0.75,
                    fontSize: '0.75rem',
                  },
                }}
              >
                <MenuItem value="" sx={{ fontSize: '0.75rem', py: 1, color: '#9CA3AF', fontStyle: 'italic' }}>
                  None (Unassigned)
                </MenuItem>
                {options?.map((option) => (
                  <MenuItem key={option.value} value={option.value.toString()} sx={{ fontSize: '0.75rem', py: 1 }}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {option.label}
                      </Typography>
                      {option.subtitle && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {option.subtitle}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              type={type}
              size="small"
              value={tempValue}
              onChange={(e) => onChange(e.target.value)}
              sx={{
                flex: 1,
                minWidth: 0,
                "& .MuiOutlinedInput-root": {
                  fontSize: "0.75rem",
                  height: "32px",
                },
                "& .MuiInputBase-input": {
                  py: 0.75,
                  fontSize: "0.75rem",
                },
              }}
            />
          )}
          <Tooltip title="Save">
            <IconButton
              size="small"
              onClick={onSave}
              disabled={updating}
              sx={{
                color: "#10B981",
                flexShrink: 0,
                "&:hover": { bgcolor: "rgba(16, 185, 129, 0.1)" },
              }}
            >
              <SaveIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton
              size="small"
              onClick={onCancel}
              sx={{
                color: theme.customColors?.text?.secondary,
                flexShrink: 0,
                "&:hover": { bgcolor: theme.customColors?.background?.light },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        renderDisplay ? renderDisplay(value) : (
          <Typography variant="body2" fontWeight="600" pl={3.5}>
            {value || "-"}
          </Typography>
        )
      )}
    </Box>
  );
};

export default EditableField;
